import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Send, ChevronLeft, Phone, Video, Info } from "lucide-react";
import { logger } from "../../utils/logger";
import { scormService } from "../../utils/scormService";
import { ChatMessageContent } from "./ChatMessageContent";
import { OtpNotification } from "./OtpNotification";
import { FakeLandingPage } from "./FakeLandingPage";
import type { LandingPage, LandingPageSubmission } from "./FakeLandingPage";
import { SmishingInputHelper } from "./SmishingInputHelper";
import { buildSmishingInitRequest, buildSmishingMessageRequest, type SmishingChatInitResponse, type SmishingChatReplyResponse, type SmishingRequestCategory } from "./smishing-chat-api";

type IGStatus = "idle" | "sending" | "receiving" | "ending" | "error" | "finished";

interface IGMessage {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
}

interface InstagramChatProps {
  microlearningId: string;
  language?: string;
  endpoint?: string;
  senderName?: string;
  senderNumber?: string;
  startLabel?: string;
  statusTexts?: Partial<Record<IGStatus, string>>;
  linkWarningTitle?: string;
  linkWarningBody?: string;
  otpTexts?: { codeLabel?: string; codeSuffix?: string; backLabel?: string; backHint?: string };
  landingPageTexts?: { backLabel?: string };
  uiTexts?: {
    now?: string;
    inputPlaceholder?: string;
    online?: string;
    typing?: string;
    unknownSender?: string;
    startChat?: string;
    chatEnded?: string;
    errorGeneric?: string;
    errorStartChat?: string;
    errorGetReply?: string;
  };
  embedded?: boolean;
  className?: string;
  onStatusChange?: (status: IGStatus) => void;
  onCompletionChange?: (completed: boolean) => void;
}

const FALLBACK_SMISHING_ENDPOINT = "https://agentic-ai-agent.keepnetlabs.com/smishing/chat";
const DEFAULT_SMISHING_ENDPOINT = typeof window !== "undefined" && (
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
) ? "http://localhost:4111/smishing/chat" : FALLBACK_SMISHING_ENDPOINT;

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("accessToken");
};

const formatTime = () => {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Instagram typing indicator
function TypingBubble() {
  return (
    <div className="flex justify-start">
      <div className="rounded-full bg-[#EFEFEF] px-3 py-2">
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#8E8E8E] [animation-delay:0ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#8E8E8E] [animation-delay:200ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#8E8E8E] [animation-delay:400ms]" />
        </div>
      </div>
    </div>
  );
}

// Instagram logo
function InstagramLogo({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="white">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

export function InstagramChat({
  microlearningId,
  language = "en-gb",
  endpoint = DEFAULT_SMISHING_ENDPOINT,
  senderName,
  statusTexts,
  linkWarningTitle,
  linkWarningBody,
  otpTexts,
  landingPageTexts,
  uiTexts,
  embedded = false,
  className = "",
  onStatusChange,
  onCompletionChange
}: InstagramChatProps) {
  const [status, setStatus] = useState<IGStatus>("idle");
  const [messages, setMessages] = useState<IGMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [hasStarted, setHasStarted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [otpData, setOtpData] = useState<{ code: string; sender: string } | null>(null);
  const [expectedResponseCategory, setExpectedResponseCategory] = useState<SmishingRequestCategory | undefined>(undefined);
  const shownOtpCodes = useRef<Set<string>>(new Set());
  const activeOtpRef = useRef<{ code: string; sender: string } | null>(null);
  const [activeLandingPage, setActiveLandingPage] = useState<LandingPage | null>(null);
  const landingPageDataRef = useRef<LandingPage | null>(null);
  const pendingLandingPageRef = useRef<LandingPageSubmission | null>(null);
  const clickedLinkRef = useRef<string | null>(null);
  const autoSendRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const initRequestedRef = useRef(false);

  // Get user info from SCORM LMS
  const studentInfo = useMemo(() => scormService.getStudentInfo(), []);
  const resolvedSenderName = senderName || uiTexts?.unknownSender || "Unknown";
  const userName = studentInfo.name || "You";
  const senderInitial = resolvedSenderName.charAt(0).toUpperCase();

  const updateStatus = useCallback((next: IGStatus) => {
    setStatus(next);
    onStatusChange?.(next);
  }, [onStatusChange]);

  const statusLabel = useMemo(() => {
    const defaultLabels: Record<IGStatus, string> = {
      idle: uiTexts?.startChat || "Tap to start",
      sending: "Sending\u2026",
      receiving: "typing\u2026",
      ending: "Ending\u2026",
      finished: uiTexts?.chatEnded || "Chat ended",
      error: uiTexts?.errorGeneric || "Something went wrong"
    };
    return statusTexts?.[status] ?? statusTexts?.idle ?? defaultLabels[status];
  }, [status, statusTexts, uiTexts]);

  const scrollToBottom = useCallback(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const initChat = useCallback(async () => {
    if (hasStarted || isInitializing) return;
    setIsInitializing(true);
    updateStatus("sending");
    setErrorMessage(null);
    try {
      const token = getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-AGENTIC-ALLY-TOKEN"] = token;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(buildSmishingInitRequest(microlearningId, language, "instagram"))
      });
      const data = await response.json() as SmishingChatInitResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Init failed: ${response.status}`);
      }
      if (data.firstMessage) {
        setMessages([{ role: "assistant" as const, content: data.firstMessage, timestamp: formatTime() }]);
      }
      // Build-time OTP: show notification shortly after chat opens
      if (data.otpCode && data.otpSender && !shownOtpCodes.current.has(data.otpCode)) {
        shownOtpCodes.current.add(data.otpCode);
        setOtpData({ code: data.otpCode, sender: data.otpSender });
        activeOtpRef.current = { code: data.otpCode, sender: data.otpSender };
      }
      if (data.landingPage) {
        landingPageDataRef.current = data.landingPage;
      }
      if (data.isFinished) {
        setExpectedResponseCategory(undefined);
        setIsFinished(true);
        updateStatus("finished");
        onCompletionChange?.(true);
      } else {
        setExpectedResponseCategory(data.expectedResponseCategory);
        setHasStarted(true);
        updateStatus("idle");
      }
    } catch (error) {
      updateStatus("error");
      setErrorMessage(uiTexts?.errorStartChat || "Failed to start chat.");
      logger.push({ level: "error", code: "IG_INIT_FAIL", message: "Failed to init instagram chat", detail: String(error) });
    } finally {
      setIsInitializing(false);
    }
  }, [endpoint, hasStarted, isInitializing, language, microlearningId, onCompletionChange, uiTexts?.errorStartChat, updateStatus]);

  useEffect(() => {
    if (initRequestedRef.current) return;
    initRequestedRef.current = true;
    initChat();
  }, [initChat]);

  const sendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || status !== "idle" || !hasStarted || isFinished) return;
    const nextMessages: IGMessage[] = [...messages, { role: "user" as const, content: trimmed, timestamp: formatTime() }];
    setMessages(nextMessages);
    setInputValue("");
    setExpectedResponseCategory(undefined);
    updateStatus("receiving");
    setErrorMessage(null);
    try {
      const token = getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-AGENTIC-ALLY-TOKEN"] = token;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(buildSmishingMessageRequest({
          microlearningId,
          language,
          channel: "instagram",
          messages: nextMessages,
          pendingOtp: activeOtpRef.current,
          pendingLandingPageSubmission: pendingLandingPageRef.current,
          pendingClickedLink: clickedLinkRef.current,
        }))
      });
      pendingLandingPageRef.current = null;
      clickedLinkRef.current = null;
      const data = await response.json() as SmishingChatReplyResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Chat failed: ${response.status}`);
      }
      const replyParts = data.replies && data.replies.length > 1 ? data.replies : data.reply ? [data.reply] : [];
      if (replyParts.length > 0) {
        // First message immediately
        let accumulated: IGMessage[] = [...nextMessages, { role: "assistant" as const, content: replyParts[0], timestamp: formatTime() }];
        setMessages(accumulated);

        // Subsequent messages with staggered delay (typing effect)
        for (let i = 1; i < replyParts.length; i++) {
          updateStatus("receiving");
          await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1500));
          accumulated = [...accumulated, { role: "assistant" as const, content: replyParts[i], timestamp: formatTime() }];
          setMessages(accumulated);
        }
      }
      // Trigger OTP notification if backend sent one
      if (data.otpCode && data.otpSender && !shownOtpCodes.current.has(data.otpCode)) {
        shownOtpCodes.current.add(data.otpCode);
        setOtpData({ code: data.otpCode, sender: data.otpSender });
        activeOtpRef.current = { code: data.otpCode, sender: data.otpSender };
      }
      // Store landing page data — shown only when user taps the link
      landingPageDataRef.current = data.landingPage || null;
      if (data.isFinished) {
        setExpectedResponseCategory(undefined);
        setIsFinished(true);
        updateStatus("finished");
        onCompletionChange?.(true);
      } else {
        setExpectedResponseCategory(data.expectedResponseCategory);
        updateStatus("idle");
      }
    } catch (error) {
      updateStatus("error");
      setErrorMessage(uiTexts?.errorGetReply || "Failed to get reply.");
      logger.push({ level: "error", code: "IG_CHAT_FAIL", message: "Failed to fetch instagram reply", detail: String(error) });
    }
  }, [endpoint, hasStarted, inputValue, isFinished, language, messages, microlearningId, onCompletionChange, status, uiTexts?.errorGetReply, updateStatus]);

  const handleLinkClick = useCallback((url: string): boolean => {
    clickedLinkRef.current = url;
    if (landingPageDataRef.current && landingPageDataRef.current.url === url) {
      setActiveLandingPage(landingPageDataRef.current);
      return true;
    }
    return false;
  }, []);

  const handleLandingPageSubmit = useCallback((submission: LandingPageSubmission) => {
    pendingLandingPageRef.current = submission;
    clickedLinkRef.current = null;
    setActiveLandingPage(null);
    setInputValue(Object.values(submission.fields).join(", "));
    autoSendRef.current = true;
  }, []);

  const handleLandingPageBack = useCallback(() => {
    setActiveLandingPage(null);
  }, []);

  // Auto-send after landing page form submission
  useEffect(() => {
    if (autoSendRef.current && inputValue.trim()) {
      autoSendRef.current = false;
      sendMessage();
    }
  }, [inputValue, sendMessage]);

  const isBusy = status === "sending" || status === "receiving" || isInitializing || isFinished;
  const previewText = messages[0]?.content || statusLabel;
  const wrapperClassName = embedded ? `w-full ${className}` : `w-full max-w-[420px] glass-border-3 p-4 ${className}`;
  const chatShellClassName = embedded
    ? "flex h-full min-h-0 flex-col overflow-hidden rounded-lg bg-white"
    : "flex h-full min-h-[420px] flex-col overflow-hidden rounded-lg bg-white";

  return (
    <div className={wrapperClassName}>
      {!isOpen ? (
        <div className="relative flex min-h-[380px] flex-col items-center justify-center">
          <button
            type="button"
            onClick={() => { setIsOpen(true); if (!hasStarted) initChat(); }}
            className="group relative w-full max-w-[270px] overflow-hidden rounded-[18px] border border-white/30 bg-white/80 p-3 text-left shadow-[0_4px_24px_rgba(0,0,0,0.08)] backdrop-blur-xl transition-all duration-200 hover:scale-[1.01] hover:shadow-[0_8px_32px_rgba(0,0,0,0.12)] active:scale-[0.99] dark:border-white/10 dark:bg-[#2C2C2E]/90 dark:shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
          >
            <div className="relative flex items-center gap-2">
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] shadow-sm" style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}>
                <InstagramLogo />
              </div>
              <span className="text-[13px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">Instagram</span>
              <span className="ml-auto text-[12px] text-[#8E8E93] dark:text-[#98989D]">{uiTexts?.now || "now"}</span>
            </div>
            <div className="relative mt-1.5 pl-[44px]">
              <div className="flex items-center justify-between">
                <span className="text-[14px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">{resolvedSenderName}</span>
                <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white" style={{ background: "linear-gradient(45deg, #f09433, #dc2743)" }}>1</span>
              </div>
              <div className="mt-0.5 pr-3 text-[14px] leading-snug text-[#3C3C43]/70 line-clamp-2 dark:text-[#EBEBF5]/60">
                {previewText}
              </div>
            </div>
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
            <div className="h-1 w-28 rounded-full bg-black/15 dark:bg-white/20" />
          </div>
        </div>
      ) : (
        <div className={`${chatShellClassName} relative`}>
          {/* OTP Notification overlay */}
          {otpData && (
            <OtpNotification
              otpCode={otpData.code}
              otpSender={otpData.sender}
              codeLabel={otpTexts?.codeLabel}
              codeSuffix={otpTexts?.codeSuffix}
              backLabel={otpTexts?.backLabel}
              backHint={otpTexts?.backHint}
              nowLabel={uiTexts?.now}
            />
          )}
          {activeLandingPage && (
            <FakeLandingPage
              landingPage={activeLandingPage}
              onSubmit={handleLandingPageSubmit}
              onBack={handleLandingPageBack}
              backLabel={landingPageTexts?.backLabel}
            />
          )}
          {/* Instagram DM header */}
          <div className="shrink-0 flex items-center gap-2 border-b border-[#DBDBDB] px-2 py-2">
            <ChevronLeft size={18} className="shrink-0 text-[#262626]" />
            {/* Avatar with gradient ring */}
            <div className="h-8 w-8 shrink-0 rounded-full p-[2px]" style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}>
              <div className="flex h-full w-full items-center justify-center rounded-full bg-white text-xs font-bold text-[#262626]">
                {senderInitial}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-[#262626] leading-tight truncate">{resolvedSenderName}</div>
              <div className="text-[11px] text-[#8E8E8E] leading-tight">
                {status === "receiving" ? "Typing..." : "Active now"}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone size={16} className="text-[#262626]" />
              <Video size={18} className="text-[#262626]" />
              <Info size={18} className="text-[#262626]" />
            </div>
          </div>

          {/* Chat area */}
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-3 bg-white">
            {messages.length === 0 && !isInitializing && (
              <div className="flex flex-col items-center justify-center py-6 gap-2">
                <div className="h-14 w-14 rounded-full p-[2px]" style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}>
                  <div className="flex h-full w-full items-center justify-center rounded-full bg-white text-lg font-bold text-[#262626]">
                    {senderInitial}
                  </div>
                </div>
                <span className="text-[14px] font-semibold text-[#262626]">{resolvedSenderName}</span>
                <span className="text-[12px] text-[#8E8E8E]">Instagram</span>
              </div>
            )}

            <div className="flex flex-col gap-1">
              {messages.map((msg, idx) => (
                <div
                  key={`${msg.role}-${idx}`}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.role === "assistant" && (
                    <div className="mr-1.5 mt-auto h-6 w-6 shrink-0 rounded-full bg-[#EFEFEF] flex items-center justify-center text-[10px] font-bold text-[#262626]">
                      {senderInitial}
                    </div>
                  )}
                  <div
                    className={`max-w-[70%] break-words [overflow-wrap:anywhere] rounded-full px-3 py-2 text-[14px] leading-snug ${
                      msg.role === "user"
                        ? "bg-[#3797F0] text-white"
                        : "bg-[#EFEFEF] text-[#262626]"
                    }`}
                  >
                    <ChatMessageContent
                      content={msg.content}
                      linkWarningTitle={linkWarningTitle}
                      linkWarningBody={linkWarningBody}
                      onLinkClick={handleLinkClick}
                    />
                  </div>
                </div>
              ))}
              {/* Seen indicator on last user message */}
              {messages.length > 0 && messages[messages.length - 1].role === "user" && status === "idle" && (
                <div className="flex justify-end pr-1">
                  <span className="text-[10px] text-[#8E8E8E]">Seen by {userName}</span>
                </div>
              )}
              {status === "receiving" && <TypingBubble />}
            </div>
          </div>

          {/* Input area - Instagram style */}
          <SmishingInputHelper
            category={expectedResponseCategory}
            language={language}
            disabled={isBusy || !hasStarted}
            onSelect={setInputValue}
          />
          <div className="shrink-0 border-t border-[#DBDBDB] px-2 pb-2 pt-1.5 bg-white">
            <div className="flex items-center gap-1.5 rounded-full border border-[#DBDBDB] px-3 py-1.5">
              <input
                type="text"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && sendMessage()}
                placeholder={hasStarted ? (uiTexts?.inputPlaceholder || "Message...") : statusLabel}
                disabled={isBusy || !hasStarted}
                className="flex-1 bg-transparent text-[14px] text-[#262626] placeholder-[#8E8E8E] focus:outline-none"
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={!inputValue.trim() || isBusy || !hasStarted}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                  inputValue.trim() && !isBusy && hasStarted
                    ? "bg-[#3797F0] text-white"
                    : "text-[#C7C7CC]"
                } disabled:opacity-40`}
                aria-label="Send message"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="mt-2 text-xs text-rose-600 dark:text-rose-300">{errorMessage}</div>
      )}
    </div>
  );
}
